import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scaleLabels } from '../data/questionnaireData';

const QuestionnairePage = ({
  pageData,
  pageNumber,
  responses,
  onUpdateResponses,
  onPageComplete,
  isLastPage,
  allPagesCompleted
}) => {
  const navigate = useNavigate();
  const [currentResponses, setCurrentResponses] = useState(responses);
  const [showValidationError, setShowValidationError] = useState(false);

  // Update local state when responses prop changes
  useEffect(() => {
    setCurrentResponses(responses);
  }, [responses]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageNumber]);

  const handleResponseChange = (questionId, value, questionType) => {
    let processedValue = value;
    
    // Process value based on question type
    if (questionType === 'scale') {
      processedValue = parseInt(value);
    } else if (questionType === 'number') {
      processedValue = parseFloat(value) || value; // Keep as string if not a valid number
    } else if (questionType === 'yes_no') {
      processedValue = value === 'true';
    }
    // dropdown and other types remain as strings
    
    const updatedResponses = {
      ...currentResponses,
      [questionId]: processedValue
    };
    setCurrentResponses(updatedResponses);
    onUpdateResponses(updatedResponses);
    
    // Clear validation error if it exists
    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
  };

  const handleCurrencyChange = (questionId, value) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.-]/g, '');
    const parsedValue = parseFloat(numericValue) || numericValue;
    
    const updatedResponses = {
      ...currentResponses,
      [questionId]: parsedValue
    };
    setCurrentResponses(updatedResponses);
    onUpdateResponses(updatedResponses);
    
    // Clear validation error if it exists
    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const validatePage = () => {
    const requiredQuestions = pageData.questions.filter(q => q.required);
    const answeredQuestions = requiredQuestions.filter(q => {
      const response = currentResponses[q.id];
      return response !== undefined && response !== null && response !== '';
    });
    return answeredQuestions.length === requiredQuestions.length;
  };

  const handleNext = () => {
    if (!validatePage()) {
      setShowValidationError(true);
      return;
    }

    onPageComplete();
    
    // If this is the last page, go to completion page
    if (isLastPage) {
      navigate('/complete');
    } else {
      navigate(`/page/${pageNumber + 1}`);
    }
  };

  const handlePrevious = () => {
    if (pageNumber > 1) {
      navigate(`/page/${pageNumber - 1}`);
    }
  };

  const getProgressPercentage = () => {
    const totalQuestions = pageData.questions.length;
    const answeredQuestions = Object.keys(currentResponses).filter(key => {
      const response = currentResponses[key];
      return response !== undefined && response !== null && response !== '';
    }).length;
    return (answeredQuestions / totalQuestions) * 100;
  };

  const getUnansweredQuestions = () => {
    return pageData.questions.filter(q => {
      if (!q.required) return false;
      const response = currentResponses[q.id];
      return response === undefined || response === null || response === '';
    });
  };

  const renderQuestionInput = (question, index) => {
    const questionType = question.type || 'scale'; // default to scale for backward compatibility
    const currentValue = currentResponses[question.id];

    switch (questionType) {
      case 'dropdown':
        return (
          <div className="dropdown-container">
            <select
              value={currentValue || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value, 'dropdown')}
              className="dropdown-select"
            >
              {question.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'number':
        return (
          <div className="number-container">
            <input
              type="number"
              value={currentValue || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value, 'number')}
              placeholder={question.placeholder || 'Enter a number'}
              className="number-input"
              step="any"
            />
          </div>
        );

      case 'text':
        return (
          <div className="text-container">
            <input
              type="text"
              value={currentValue || ''}
              onChange={(e) => handleResponseChange(question.id, e.target.value, 'text')}
              placeholder={question.placeholder || 'Enter text'}
              className="text-input"
            />
          </div>
        );

      case 'currency':
        return (
          <div className="currency-container">
            <div className="currency-input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="text"
                value={formatCurrency(currentValue) || ''}
                onChange={(e) => handleCurrencyChange(question.id, e.target.value)}
                placeholder="0"
                className="currency-input"
                onFocus={(e) => {
                  // Remove formatting on focus for easier editing
                  e.target.value = currentValue || '';
                }}
                onBlur={(e) => {
                  // Reformat on blur
                  e.target.value = formatCurrency(currentValue) || '';
                }}
              />
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="yes-no-container">
            <div className="yes-no-options">
              <label className="yes-no-option">
                <input
                  type="radio"
                  name={question.id}
                  value="true"
                  checked={currentValue === true}
                  onChange={(e) => handleResponseChange(question.id, e.target.value, 'yes_no')}
                />
                <span className="yes-no-label yes">Yes</span>
              </label>
              <label className="yes-no-option">
                <input
                  type="radio"
                  name={question.id}
                  value="false"
                  checked={currentValue === false}
                  onChange={(e) => handleResponseChange(question.id, e.target.value, 'yes_no')}
                />
                <span className="yes-no-label no">No</span>
              </label>
            </div>
          </div>
        );

      case 'scale':
      default:
        return (
          <div className="scale-container">
            <div className="scale-labels">
              <span className="scale-label-left">{scaleLabels[1]}</span>
              <span className="scale-label-right">{scaleLabels[6]}</span>
            </div>
            
                          <div className="scale-options">
                {[1, 2, 3, 4, 5, 6].map((value) => (
                  <label key={value} className="scale-option">
                    <input
                      type="radio"
                      name={question.id}
                      value={value}
                      checked={currentResponses[question.id] === value}
                      onChange={(e) => handleResponseChange(question.id, e.target.value, 'scale')}
                    />
                    <span className="scale-number">{value}</span>
                    <span className="scale-description">{scaleLabels[value]}</span>
                  </label>
                ))}
              </div>
          </div>
        );
    }
  };

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <div className="page-info">
          <h2>{pageData.title}</h2>
          <p className="page-description">{pageData.description}</p>
          <div className="page-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
            <span className="progress-text">
              {Object.keys(currentResponses).filter(key => {
                const response = currentResponses[key];
                return response !== undefined && response !== null && response !== '';
              }).length} of {pageData.questions.length} questions answered
            </span>
          </div>
        </div>
        <div className="page-number">
          Page {pageNumber} of 4
        </div>
      </div>

      {showValidationError && (
        <div className="validation-error">
          <h4>Please answer all questions before proceeding:</h4>
          <ul>
            {getUnansweredQuestions().map(question => (
              <li key={question.id}>{question.text}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="questions-container">
        {pageData.questions.map((question, index) => (
          <div key={question.id} className="question-block">
            <div className="question-header">
              <h3 className="question-number">Question {index + 1}</h3>
              {question.required && <span className="required-indicator">*</span>}
            </div>
            <p className="question-text">{question.text}</p>
            
            {question.benchmark && question.type === 'scale' && (
              <div className="benchmark-text">
                <strong>Benchmark for a 6: </strong>{question.benchmark}</div>
            )}
            
            {renderQuestionInput(question, index)}
          </div>
        ))}
      </div>

      <div className="navigation-buttons">
        <button 
          className="btn btn-secondary" 
          onClick={handlePrevious}
          disabled={pageNumber === 1}
        >
          Previous
        </button>
        
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
        >
          {isLastPage ? 'Complete Survey' : 'Next Page'}
        </button>
      </div>
    </div>
  );
};

export default QuestionnairePage; 